import { FC, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useOpenRideInNewTab } from "@ryde/redux";
import {
  collectRelatedHubRideIdsBySegment,
  IRideDto,
  onActivateKeyDown,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

export const HUB_TOOLTIP_MOUSE_LEAVE_DELAY = 300;

interface IHubRelatedRideTooltipContentProps {
  rideDto: Partial<IRideDto> | undefined;
  segment: "main" | "personal";
  hasCompanionPassengers?: boolean;
}

export const HubRelatedRideTooltipContent: FC<
  IHubRelatedRideTooltipContentProps
> = ({ rideDto, segment, hasCompanionPassengers }) => {
  const { t } = useTranslation();
  const { openRideInNewTab } = useOpenRideInNewTab();
  const { relatedMainRideIds, relatedPersonalRideIds } =
    collectRelatedHubRideIdsBySegment(rideDto);

  const label =
    segment === "main" ? t("ride.HUB_MAIN") : t("ride.HUB_PERSONAL");
  const openLabelKey =
    segment === "main" ? "ride.OPEN_PERSONAL_HUB" : "ride.OPEN_MAIN_HUB";
  const relatedRideIds =
    segment === "main" ? relatedPersonalRideIds : relatedMainRideIds;

  const handleOpen = useEvent((rideId: number, event?: MouseEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    void openRideInNewTab(rideId);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div>{label}</div>
      {hasCompanionPassengers && (
        <div>{t("ride.HUB_HAS_COMPANION_PASSENGERS")}</div>
      )}
      {relatedRideIds.map(rideId => (
        <div key={rideId}>
          <span
            role="button"
            tabIndex={0}
            style={{
              color: rydeColors.dragBorderColor,
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={event => handleOpen(rideId, event)}
            onKeyDown={onActivateKeyDown(() => handleOpen(rideId))}
          >
            {t(openLabelKey, { rideId })}
          </span>
        </div>
      ))}
    </div>
  );
};
